import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, Trash2, Edit3, Check, X, RotateCcw, Users, Tag as TagIcon } from 'lucide-react';
import { Tag } from '../types/tag';
import { Lead } from '../types/lead';
import { createTag, updateTag, deleteTag } from '../lib/tagsManagement';
import PageHeader from '../components/Layout/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import UserList from '../components/Admin/UserManagement/UserList';
import UserEditModal from '../components/Admin/UserManagement/UserEditModal';
import CreateUserModal from '../components/Admin/UserManagement/CreateUserModal';
import PushNotificationToggle from '../components/PushNotificationToggle';
import { Profile } from '../types/user';
import { updateUser, createUser, resetUserPassword } from '../lib/userManagement';
import { useAuth } from '../contexts/AuthContext';

interface SetupPageProps {
  tags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  archivedLeads: Lead[];
  onRestoreLead: (leadId: string) => void;
  onDeleteArchivedLead: (leadId: string) => void;
  isAdmin: boolean;
  onResetPassword: (email: string) => void;
  onImpersonate: (userId: string) => void;
}

type SetupSection = 'tags' | 'archive' | 'users' | 'notifications';

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#22c55e',
  '#10b981', '#3b82f6', '#8b5cf6', '#ec4899',
  '#6b7280', '#1e293b',
];

const SetupPage: React.FC<SetupPageProps> = ({
  tags,
  onTagsChange,
  archivedLeads,
  onRestoreLead,
  onDeleteArchivedLead,
  isAdmin,
  onResetPassword,
  onImpersonate,
}) => {
  const [activeSection, setActiveSection] = useState<SetupSection>('tags');
  const { currentUser } = useAuth();
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3b82f6');
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [confirmDeleteTagId, setConfirmDeleteTagId] = useState<string | null>(null);
  const [confirmDeleteLeadId, setConfirmDeleteLeadId] = useState<string | null>(null);

  const handleCreateTag = async () => {
    if (!newTagName.trim() || saving) return;
    setSaving(true);
    const tag = await createTag(newTagName.trim(), newTagColor);
    if (tag) {
      onTagsChange([...tags, tag]);
      setNewTagName('');
      setNewTagColor('#3b82f6');
    }
    setSaving(false);
  };

  const handleDeleteTag = async (id: string) => {
    await deleteTag(id);
    onTagsChange(tags.filter(t => t.id !== id));
    setConfirmDeleteTagId(null);
  };

  const handleStartEdit = (tag: Tag) => {
    setEditingTag(tag);
    setEditTagName(tag.name);
    setEditTagColor(tag.color);
  };

  const handleSaveEdit = async () => {
    if (!editingTag || !editTagName.trim()) return;
    const ok = await updateTag(editingTag.id, { name: editTagName.trim(), color: editTagColor });
    if (ok) {
      onTagsChange(tags.map(t => t.id === editingTag.id ? { ...t, name: editTagName.trim(), color: editTagColor } : t));
      setEditingTag(null);
    }
  };

  const handleCreateUser = async (userData: Omit<Profile, 'id' | 'created_at'> & { password: string }) => {
    const result = await createUser({
      email: userData.email,
      full_name: userData.full_name,
      password: userData.password,
      role: userData.role,
      calcom_url: userData.calcom_url,
    });
    if (result.success) {
      setShowCreateUser(false);
    } else {
      alert(`Fehler: ${result.error}`);
    }
  };

  const handleSaveUser = async (userId: string, updates: Partial<Profile>) => {
    const ok = await updateUser(userId, updates);
    if (ok) setEditingUser(null);
    else alert('Fehler beim Speichern');
  };

  const handleDeleteUser = async (userId: string) => {
    await updateUser(userId, { is_active: false });
    setEditingUser(null);
  };

  const SECTIONS: { id: SetupSection; label: string; adminOnly?: boolean }[] = [
    { id: 'tags', label: 'Labels' },
    { id: 'archive', label: 'Archiv' },
    { id: 'notifications', label: 'Benachrichtigungen' },
    { id: 'users', label: 'Benutzer', adminOnly: true },
  ];

  return (
    <div>
      <PageHeader title="Setup" subtitle="Einstellungen & Verwaltung" />

      <div className="px-4 md:px-8 py-4 space-y-4">
        {/* Section Tabs */}
        <div className="flex gap-2">
          {SECTIONS.filter(s => !s.adminOnly || isAdmin).map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeSection === s.id ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* TAG MANAGER */}
        {activeSection === 'tags' && (
          <div className="space-y-4">
            {/* Create Tag */}
            <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Neues Label</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newTagName}
                  onChange={e => setNewTagName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateTag()}
                  placeholder="Label-Name..."
                  className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
                <input
                  type="color"
                  value={newTagColor}
                  onChange={e => setNewTagColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-1"
                />
                <button
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim() || saving}
                  className="px-4 py-2 bg-zinc-900 text-white text-sm font-semibold rounded-lg disabled:opacity-40 hover:bg-zinc-800 flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  Erstellen
                </button>
              </div>
              {/* Color Presets */}
              <div className="flex gap-2 flex-wrap">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setNewTagColor(c)}
                    className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${newTagColor === c ? 'ring-2 ring-offset-2 ring-zinc-900' : ''}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            {/* Tags List */}
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              {tags.length === 0 ? (
                <p className="text-center text-zinc-400 text-sm py-8">Noch keine Labels</p>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {tags.map(tag => (
                    <div key={tag.id} className="flex items-center gap-3 px-4 py-3">
                      {editingTag?.id === tag.id ? (
                        <>
                          <input
                            type="color"
                            value={editTagColor}
                            onChange={e => setEditTagColor(e.target.value)}
                            className="w-8 h-8 rounded border border-zinc-200 cursor-pointer p-0.5"
                          />
                          <input
                            type="text"
                            value={editTagName}
                            onChange={e => setEditTagName(e.target.value)}
                            className="flex-1 border border-zinc-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                            autoFocus
                          />
                          <button onClick={handleSaveEdit} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingTag(null)} className="p-1.5 text-zinc-400 hover:bg-zinc-100 rounded-lg">
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                          <span
                            className="flex-1 text-sm font-medium px-2.5 py-1 rounded-full"
                            style={{ backgroundColor: tag.color + '20', color: tag.color }}
                          >
                            {tag.name}
                          </span>
                          <button onClick={() => handleStartEdit(tag)} className="p-1.5 text-zinc-400 hover:bg-zinc-100 rounded-lg transition-colors">
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setConfirmDeleteTagId(tag.id)} className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ARCHIVE */}
        {activeSection === 'archive' && (
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-100">
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">
                Archivierte Anfragen ({archivedLeads.length})
              </h3>
            </div>
            {archivedLeads.length === 0 ? (
              <p className="text-center text-zinc-400 text-sm py-8">Keine archivierten Anfragen</p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {archivedLeads.map(lead => (
                  <div key={lead.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 text-sm font-bold flex-shrink-0">
                      {lead.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-800 truncate">{lead.name}</p>
                      <p className="text-xs text-zinc-400 truncate">{lead.email}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => onRestoreLead(lead.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-600 transition-colors"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Wiederherstellen
                      </button>
                      <button
                        onClick={() => setConfirmDeleteLeadId(lead.id)}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* NOTIFICATIONS */}
        {activeSection === 'notifications' && currentUser && (
          <div className="space-y-4">
            <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Push-Benachrichtigungen</h3>
              <p className="text-sm text-zinc-500">Erhalte eine Benachrichtigung auf deinem Gerät, sobald eine neue Anfrage eingeht.</p>
              <PushNotificationToggle userId={currentUser.id} />
            </div>
          </div>
        )}

        {/* USERS */}
        {activeSection === 'users' && isAdmin && (
          <div>
            <UserList
              onImpersonate={onImpersonate}
              onEditUser={setEditingUser}
              onCreateUser={() => setShowCreateUser(true)}
              onResetPassword={onResetPassword}
            />
          </div>
        )}
      </div>

      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onCreate={handleCreateUser}
        />
      )}

      {editingUser && (
        <UserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveUser}
          onDelete={handleDeleteUser}
          onResetPassword={onResetPassword}
        />
      )}

      <ConfirmDialog
        open={confirmDeleteTagId !== null}
        title="Label löschen?"
        message="Dieses Label wird von allen Anfragen entfernt und kann nicht wiederhergestellt werden."
        onConfirm={() => confirmDeleteTagId && handleDeleteTag(confirmDeleteTagId)}
        onCancel={() => setConfirmDeleteTagId(null)}
      />

      <ConfirmDialog
        open={confirmDeleteLeadId !== null}
        title="Anfrage endgültig löschen?"
        message="Diese Anfrage wird permanent gelöscht und kann nicht wiederhergestellt werden."
        confirmLabel="Endgültig löschen"
        onConfirm={() => { if (confirmDeleteLeadId) { onDeleteArchivedLead(confirmDeleteLeadId); setConfirmDeleteLeadId(null); } }}
        onCancel={() => setConfirmDeleteLeadId(null)}
      />
    </div>
  );
};

export default SetupPage;
